import { Fragment, useEffect, useMemo, useState } from "react";
import { useScrollbarSync } from "../hooks/useScrollbarSync";
import { usePaginatedSearch } from "../hooks/usePaginatedSearch";
import ArtApi from "../api/ArtApi";
import { Grid, Table, CircularProgress, TableCell, TableRow, Paper, Button, TableBody, makeStyles } from "@material-ui/core";
import { formatUTC } from "../lib/helpers";
import { buildCsvRow } from "../lib/csv";
import { EditableCell } from "./EditableFields";
import { SearchBar } from "./SearchBar";
import ErrorSnackbar from "./ErrorSnackbar";

const useStyles = makeStyles({
    scrollWrapper: {
        width: '100%',
    },
    topScrollbar: {
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        marginBottom: '10px',
    },
    topScrollbarInner: {
        height: '1px',
    },
    tableContainer: {
        width: '100%',
        overflowX: 'auto',
        /* Hide scrollbar for Chrome, Safari and Opera */
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        /* Hide scrollbar for IE, Edge and Firefox */
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
    },
    table: {
        minWidth: 'max-content',
        '& td, & th': {
            fontSize: 'clamp(0.7rem, 1.5vw, 0.875rem)',
            padding: '8px 12px',
            verticalAlign: 'middle',
        },
    },
    noteCell: {
        maxWidth: '300px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    }
});

export const RheemNotes = () => {
    const classes = useStyles();
    const [notes, setNotes] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const { topScrollRef, bottomScrollRef, topScrollInnerRef } = useScrollbarSync(notes);

    function loadNotes() {
        setIsLoading(true);
        ArtApi.getMechanicalTurkNotes(committedSearchTerm, NOTES_PER_PAGE, page).then(response => {
            setNotes(response.response.notes);
            if (response.error) {
                setError(response.error);
                setSnackbarOpen(true);
            }
            let { total, notes, pageSize } = response.response;
            setTotalPages(Math.ceil(total / pageSize));
            setTotal(total);
            setNotes(notes);
        }).catch(error => {
            setError(error.message);
            setSnackbarOpen(true);
        }).finally(() => {
            setIsLoading(false);
        });
    }

    const NOTES_PER_PAGE = 15;

    const { searchTerm, setSearchTerm, committedSearchTerm, doSearch, clearSearch } = usePaginatedSearch({
        page, setPage, load: loadNotes
    });

    useEffect(() => {
        loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, committedSearchTerm]);

    // Render the rows of notes
    const renderRows = useMemo(() => {
       
        if(notes) {
            
            return notes.map((note: any) => {
                let timestamp = '';
                if (note.timestamp) {
                    // note.timestamp is already in milliseconds from the backend
                    timestamp = formatUTC(Math.floor(note.timestamp / 1000));
                }
                // note.created_at is already in milliseconds from the backend
                const createdAt = formatUTC(Math.floor(note.created_at / 1000));
                const noteString = note.note;
                const core_id = <a href={`/rheem/${note.core_id}`}>{note.core_id}</a>;

                
                return (
                    <TableRow key={note.id}>
                        <EditableCell value={noteString} onBlur={(newValue => {
                            ArtApi.updateNote(note.id, noteString, note.timestamp).then(response => {
                                console.log("Update response:", response);
                                loadNotes();
                            }).catch(error => {
                                setError(error.message);
                                setSnackbarOpen(true);
                            });
                        })} />
                        <TableCell>{timestamp}</TableCell>
                        <TableCell>{createdAt}</TableCell>
                        <TableCell>{core_id}</TableCell>
                    </TableRow>
                );
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notes]);

    // Render the page selector row at the bottom
    const renderPaginationRow = useMemo(() => {
        if (totalPages === 0) {
            return null;
        }

        let prevArrow;
        if (page === 1) {
            prevArrow = <span style={{ color: 'lightgrey', fontSize: '2em' }}>&#x25C0;</span>;
        } else {
            prevArrow = <span style={{ cursor: 'pointer', fontSize: '2em' }}>&#x25C0;</span>;
        }

        let nextArrow;
        if (page >= totalPages) {
            nextArrow = <span style={{ color: 'lightgrey', fontSize: '2em' }}>&#x25B6;</span>;
        } else {
            nextArrow = <span style={{ cursor: 'pointer', color: 'lightblue', fontSize: '2em' }}>&#x25B6;</span>;
        }

        return (
            <TableRow>
                <TableCell colSpan={7}>
                    <span onClick={() => setPage(page - 1)}>{prevArrow}</span>
                    <span style={{ fontSize: '2em' }}> {page} / {totalPages} </span>
                    <span onClick={() => setPage(page + 1)}>{nextArrow}</span>
                    <span> {total} results</span>
                </TableCell>
            </TableRow>
        )
    }, [totalPages, page, total]);

    /**
     * Handle the export notes button click.
     */
    const exportNotes = () => {
        console.log("Exporting selected notes.");

        // Get the current dataset out of the ART back end API
        ArtApi.getMechanicalTurkNotes(committedSearchTerm, total, page).then(response => {
            console.log("Exported notes:", response.response.notes);

            // process the notes and create the CSV data
            let notes = response.response.notes;
            let csv = "data:text/csv;charset=utf-8,";
            csv += buildCsvRow(["Note", "Timestamp", "Created At", "Core ID"]);
            notes.forEach((note: any) => {
                const timestamp = note.timestamp ? formatUTC(Math.floor(note.timestamp / 1000)) : "";
                const createdAt = note.created_at ? formatUTC(Math.floor(note.created_at / 1000)) : "";
                csv += buildCsvRow([note.note, timestamp, createdAt, note.core_id]);
            });

            // Encode and save the CSV data.
            // PMV: This seems like a kludgey way of doing things, but it's how other export
            // features work.
            const encodedUri = encodeURI(csv);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "rheem_notes.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }).catch(error => {
            setError(error.message);
            setSnackbarOpen(true);
        });
    }

    return (
        <Fragment>
            {isLoading && <CircularProgress color="secondary" />}
            <ErrorSnackbar message={error} open={snackbarOpen} onClose={() => setSnackbarOpen(false)} />

            {!isLoading && notes &&
                <Fragment>
                    <Grid container spacing={3}>
                        <SearchBar
                            placeholder="Search for note"
                            searchTerm={searchTerm}
                            onSearchTermChange={setSearchTerm}
                            onSearch={doSearch}
                            onClearSearch={clearSearch}
                        />
                        <Grid item>
                            <Button variant="contained" onClick={exportNotes}>Export</Button>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <div className={classes.scrollWrapper}>
                                <div ref={topScrollRef} className={classes.topScrollbar}>
                                    <div ref={topScrollInnerRef} className={classes.topScrollbarInner}></div>
                                </div>
                                <Paper ref={bottomScrollRef} className={classes.tableContainer}>
                                    <Table className={classes.table}>
                                        <TableBody>
                                        <TableRow>
                                            <TableCell>Note</TableCell>
                                            <TableCell>Timestamp</TableCell>
                                            <TableCell>Created At</TableCell>
                                            <TableCell>Core ID</TableCell>
                                        </TableRow>
                                        {renderRows}
                                        {renderPaginationRow}
                                        </TableBody>
                                    </Table>
                                </Paper>
                            </div>
                        </Grid>
                    </Grid>
                </Fragment>
            }
        </Fragment>
    )
}